import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { useNavigate } from 'react-router';
import { NavLink, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import useEditAccount from './hooks/useEditAccount';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import PageShell from '@/src/Common/components/PageShell';

interface AccountObject {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    role: number;
}

export default function EditAccount() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [account, setAccount] = useState<AccountObject | null>(null);
    const authenticatedApi = useAuthenticatedApi();
    const { editAccount } = useEditAccount();
    const { id } = useParams();

    useEffect(() => {
        let isMounted = true;
        const getAccounts = async () => {
            try {
                const response: any = await authenticatedApi.get(
                    `/accounts/${id}`,
                );

                const { data } = response;

                const acccount: AccountObject = {
                    // @ts-ignore
                    id,
                    firstName: data.first_name,
                    lastName: data.last_name,
                    username: data.username,
                    email: data.email,
                    role: data.role,
                };
                setAccount(acccount);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        getAccounts();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!account) {
        return <div>account loading error, does it exist?</div>;
    }

    return (
        <PageShell
            title="Edit Account"
            breadcrumbs={[
                { label: 'Accounts', href: '/accounts' },
                { label: 'Edit Account' },
            ]}
            actions={
                <Button
                    variant="ghost"
                    onClick={() => navigate(`/accounts/${id}/password`)}
                >
                    Change Password
                </Button>
            }
        >
            <Formik
                initialValues={account}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await editAccount(values);
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        navigate('/accounts');
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <Card>
                            <CardHeader>
                                <CardTitle>Account Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="username">Username</Label>
                                        <Input
                                            id="username"
                                            name="username"
                                            value={props.values.username}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="yeah@smilebooth.com"
                                            value={props.values.email}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName">First Name</Label>
                                        <Input
                                            id="firstName"
                                            name="firstName"
                                            value={props.values.firstName}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lastName">Last Name</Label>
                                        <Input
                                            id="lastName"
                                            name="lastName"
                                            value={props.values.lastName}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="role">Role</Label>
                                        <Select
                                            value={String(props.values.role)}
                                            onValueChange={(value) => {
                                                props.setFieldValue('role', +value);
                                            }}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="2">Client</SelectItem>
                                                <SelectItem value="1">User</SelectItem>
                                                <SelectItem value="0">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="flex items-center justify-end gap-4">
                            <NavLink to="/accounts">
                                <Button type="button" variant="outline">Cancel</Button>
                            </NavLink>
                            <Button type="submit">
                                Save
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </PageShell>
    );
}
